import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Button, Message, Icon, Header,
} from 'semantic-ui-react';
import PROJECT, { RECOMMEND_PROJECT_EXPERTS } from '../../../queries/project';

const EvaluationStatusLine = ({ label, count }) => (
  count > 0 ? <p>{`${label}: ${count}`}</p> : null
);

const RecommendExperts = ({ project }) => {
  const [confirmed, setConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [recommendProjectExperts, { loading }] = useMutation(RECOMMEND_PROJECT_EXPERTS, {
    refetchQueries: [{ query: PROJECT, variables: { id: project.id } }],
    onCompleted: () => {
      setErrorMessage(null);
    },
    onError: (error) => {
      setErrorMessage(error.message);
    },
  });

  const generationStatus = project?.expertRecommendations?.generationStatus;
  const hasRecommendations = !!project?.expertRecommendations;
  const scheduledEvaluations = (
    project?.expertRecommendations?.evaluations || []
  ).filter((evaluation) => evaluation?.evaluationStatus === 'scheduled').length;
  const generatedEvaluations = (
    project?.expertRecommendations?.evaluations || []
  ).filter((evaluation) => evaluation?.evaluationStatus === 'completed').length;

  const handleGenerate = async () => {
    await recommendProjectExperts({ variables: { projectId: project.id } });
  };

  const renderContent = () => {
    if (hasRecommendations) {
      if (generationStatus === 'initiating' || generationStatus === 'running') {
        return (
          <Message icon info>
            <Icon name="circle notched" loading />
            <Message.Content>
              <Message.Header>Generating expert recommendations&hellip;</Message.Header>
              This may take a few minutes. Refresh the page to check for updates.
              <EvaluationStatusLine label="Evaluations completed" count={generatedEvaluations} />
              <EvaluationStatusLine label="Evaluations in progress" count={scheduledEvaluations} />
            </Message.Content>
          </Message>
        );
      }

      if (generationStatus === 'failed') {
        return (
          <Message negative>
            <Message.Header>Recommendation generation failed</Message.Header>
            <p>
              An error occurred while generating expert recommendations. Please contact support.
            </p>
            <EvaluationStatusLine label="Evaluations completed" count={generatedEvaluations} />
            <EvaluationStatusLine label="Evaluations in progress" count={scheduledEvaluations} />
          </Message>
        );
      }

      if (generationStatus === 'completed') {
        return (
          <Message positive>
            <Message.Header>Expert recommendations available</Message.Header>
            <p>
              {`${generatedEvaluations} evaluations are available in the `}
              <strong>Expert recommendations</strong>
              {' tab.'}
            </p>
            <EvaluationStatusLine label="Evaluations in progress" count={scheduledEvaluations} />
          </Message>
        );
      }

      // Status is initiating or unknown with recommendations present
      return (
        <Message icon info>
          <Icon name="circle notched" loading />
          <Message.Content>
            <Message.Header>Processing&hellip;</Message.Header>
            Recommendations are being prepared.
            <EvaluationStatusLine label="Evaluations completed" count={generatedEvaluations} />
            <EvaluationStatusLine label="Evaluations in progress" count={scheduledEvaluations} />
          </Message.Content>
        </Message>
      );
    }

    // No recommendations yet — show the trigger UI
    if (!confirmed) {
      return (
        <>
          <Message warning>
            <Message.Header>Before generating recommendations</Message.Header>
            <p>Please confirm the following are ready:</p>
            <Message.List>
              <Message.Item>Project description is complete and accurate</Message.Item>
              <Message.Item>Keywords are configured</Message.Item>
              <Message.Item>Qualification questions are finalized</Message.Item>
            </Message.List>
            <p>
              <strong>Note:</strong>
              {' Recommendations can only be generated once per project.'}
            </p>
          </Message>
          <Button primary onClick={() => setConfirmed(true)}>
            I&apos;m ready — Generate Recommendations
          </Button>
        </>
      );
    }

    return (
      <>
        <Message warning>
          <Message.Header>Confirm generation</Message.Header>
          <p>
            This will start the expert recommendation process for this project.
            {' This can '}
            <strong>only be done once</strong>
            .
          </p>
        </Message>
        {errorMessage && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{errorMessage}</p>
          </Message>
        )}
        <Button.Group>
          <Button onClick={() => setConfirmed(false)} disabled={loading}>
            Cancel
          </Button>
          <Button.Or />
          <Button
            positive
            loading={loading}
            disabled={loading}
            onClick={handleGenerate}
          >
            Confirm &amp; Generate
          </Button>
        </Button.Group>
      </>
    );
  };

  return (
    <div style={{ marginTop: '1em' }}>
      <Header as="h4">Recommend Experts</Header>
      {renderContent()}
    </div>
  );
};

export default RecommendExperts;
